import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { fetchKpiQbrReport } from "../../redux/slice/kpi-widget-metrics";
import { isAllProductGroupSelection } from "../MyPerformance/myPerformanceHelpers";
import { mapQbrReportToTerritoryOverview } from "./mapQbrReportToTerritoryOverview";

const EMPTY_STRATEGY_FIELDS = {
  competitiveFlags: [],
  payorDynamics: "",
  measuresOfSuccess: "",
};

export function useQBRController({ isActive = true } = {}) {
  const dispatch = useDispatch();

  const qbrReport = useSelector((state) => state.kpiWidgetMetrics.qbrReport);
  const repProductGroup = useSelector((state) => state.repEmails.productGroup);
  const selectedProduct = useSelector(
    (state) => state.repEmails.selectedProduct,
  );

  // QBR shows one product group at a time. "All" is left out, and a shared
  // "All" selection falls back to the first assigned group. The group is
  // fixed for the sales rep (not user-editable in this view).
  const productOptions = useMemo(
    () => [
      ...new Set(
        (repProductGroup || []).filter(
          (group) => group && !isAllProductGroupSelection(group),
        ),
      ),
    ],
    [repProductGroup],
  );

  const effectiveSelectedProduct = useMemo(() => {
    if (!productOptions.length) return "";
    return productOptions.includes(selectedProduct)
      ? selectedProduct
      : productOptions[0];
  }, [productOptions, selectedProduct]);

  const ranQbrFetch = useRef(false);

  useEffect(() => {
    if (!isActive) return;
    if (ranQbrFetch.current) return;
    ranQbrFetch.current = true;
    dispatch(fetchKpiQbrReport());
  }, [dispatch, isActive]);

  const refresh = useCallback(() => {
    dispatch(fetchKpiQbrReport());
  }, [dispatch]);

  // Strategy fields have no API source yet, so they live here and survive
  // product switches and refetches.
  const [strategyFields, setStrategyFields] = useState(EMPTY_STRATEGY_FIELDS);

  const overview = useMemo(
    () =>
      mapQbrReportToTerritoryOverview({
        qbrReportResponse: qbrReport?.data,
        selectedProduct: effectiveSelectedProduct,
        strategyFields,
      }),
    [qbrReport?.data, effectiveSelectedProduct, strategyFields],
  );

  const handleOverviewChange = useCallback((patch) => {
    setStrategyFields((prev) => ({ ...prev, ...patch }));
  }, []);

  return {
    overview,
    onOverviewChange: handleOverviewChange,
    isLoading: qbrReport?.isLoading ?? false,
    isError: qbrReport?.isError ?? false,
    hasData: Boolean(qbrReport?.data),
    refresh,
    productGroupLabel: effectiveSelectedProduct,
  };
}

export default useQBRController;
